import React, { useRef } from 'react';
import styled from 'styled-components';
import { Modal } from '@mui/material';
import CommonButton from '../../common/CommonButton';
import FontAwesomeIcon from '../../common/FontAwesomeIcon';
import PickRecAudio from '../../../pages/speedgrader/components/studentTask/commentsSection/PickRecAudio';
import PickRecVideo from '../../../pages/speedgrader/components/studentTask/commentsSection/PickRecVideo';

const FILE_SIZE_LIMIT_BYTES = 10485760;

const isImageUrl = (url) =>
    typeof url === 'string' && /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(url);

const isAllowedAttachmentFile = (file) => {
    if (!file) return false;
    const ok =
        file.type === 'application/pdf' ||
        file.type === 'image/jpeg' ||
        file.type === 'image/png' ||
        file.type === 'application/msword' ||
        file.type ===
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
        file.type === 'application/vnd.ms-excel' ||
        file.type ===
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        file.type === 'application/vnd.ms-powerpoint' ||
        file.type ===
            'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    return ok;
};

const EditCommentModal = (props) => {
    const {
        open,
        value,
        originalValue,
        attachmentUrls = { file: null, audio: null, video: null },
        selectedFile = null,
        selectedAudio = null,
        selectedVideo = null,
        isLoading = false,
        onChange,
        onFileChange,
        onAudioChange,
        onVideoChange,
        onClose,
        onSubmit,
        mediaPickerResetKey = 0,
    } = props;

    const fileUrl = attachmentUrls.file || null;
    const audioUrl = attachmentUrls.audio || null;
    const videoUrl = attachmentUrls.video || null;

    const hasExistingAttachments = !!(fileUrl || audioUrl || videoUrl);

    const hasMediaChange = !!(selectedFile || selectedAudio || selectedVideo);
    const hasCurrentFile = !!fileUrl;
    const hasCurrentImage = hasCurrentFile && isImageUrl(fileUrl);
    const isSaveDisabled =
        isLoading ||
        !value.trim() ||
        (value.trim() === originalValue.trim() && !hasMediaChange);

    const fileInputId = `edit-comment-modal-file-${mediaPickerResetKey}`;
    const pickAudioRef = useRef(null);
    const pickVideoRef = useRef(null);

    const handleHiddenFileChange = (event) => {
        const file = event.target.files?.[0];
        event.currentTarget.value = '';
        if (!file) return;
        if (file.size >= FILE_SIZE_LIMIT_BYTES) {
            alert('Tamaño máximo 10MB');
            return;
        }
        if (!isAllowedAttachmentFile(file)) {
            alert('Formatos de archivo inválido');
            return;
        }
        onFileChange?.(file);
    };

    const handleAudioZoneClick = (e) => {
        if (isLoading) return;
        if (e.target.closest('button')) return;
        pickAudioRef.current?.startRecording?.();
    };

    const handleVideoZoneClick = (e) => {
        if (isLoading) return;
        if (e.target.closest('button')) return;
        pickVideoRef.current?.openRecorder?.();
    };

    return (
        <StyledModal open={open} onClose={onClose} disableEnforceFocus>
            <ModalCard>
                <Header>
                    <Title>Editar comentario</Title>
                    <CloseAction
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                        aria-label="Cerrar modal"
                    >
                        <FontAwesomeIcon icon="fa-regular fa-xmark" />
                    </CloseAction>
                </Header>

                <TextAreaWrap>
                    <EditTextArea
                        value={value}
                        onChange={(event) => onChange(event.target.value)}
                        placeholder="Escribe tu comentario"
                        disabled={isLoading}
                    />
                </TextAreaWrap>

                {hasExistingAttachments && (
                    <AttachmentEditorPanel>
                        <PanelSectionLabel>Adjuntos</PanelSectionLabel>

                        <MediaBlock>
                            <BlockHeaderRow>
                                <MediaKindLabel>Archivo</MediaKindLabel>
                                {selectedFile instanceof File && (
                                    <ClearAttachmentButton
                                        type="button"
                                        onClick={() => onFileChange?.(null)}
                                        disabled={isLoading}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                                        Quitar
                                    </ClearAttachmentButton>
                                )}
                            </BlockHeaderRow>

                            {selectedFile instanceof File ? (
                                <AttachmentCurrentContent>
                                    <AttachmentFileChip
                                        title={selectedFile.name}
                                    >
                                        {selectedFile.name}
                                    </AttachmentFileChip>
                                </AttachmentCurrentContent>
                            ) : hasCurrentFile && hasCurrentImage ? (
                                <AttachmentCurrentContent>
                                    <MediaReplaceRow>
                                        <ThumbLink
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label="Ver imagen en tamaño completo"
                                        >
                                            <ReadonlyAttachmentPreview
                                                src={fileUrl}
                                                alt="Adjunto actual"
                                            />
                                        </ThumbLink>
                                        <UploadAttachmentLabel
                                            htmlFor={fileInputId}
                                        >
                                            <UploadLeft>
                                                <UploadIconWrap>
                                                    <FontAwesomeIcon icon="fa-light fa-paperclip-vertical" />
                                                </UploadIconWrap>
                                                <UploadTextWrap>
                                                    <UploadMainText>
                                                        Reemplazar archivo
                                                    </UploadMainText>
                                                    <UploadSubText>
                                                        PDF, imagen, Word, Excel
                                                        o PowerPoint
                                                    </UploadSubText>
                                                </UploadTextWrap>
                                            </UploadLeft>
                                        </UploadAttachmentLabel>
                                    </MediaReplaceRow>
                                </AttachmentCurrentContent>
                            ) : hasCurrentFile && !hasCurrentImage ? (
                                <AttachmentCurrentContent>
                                    <MediaReplaceRow>
                                        <ThumbLink
                                            href={fileUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label="Ver adjunto"
                                        >
                                            <SquareDocThumb>
                                                <FontAwesomeIcon icon="fa-solid fa-file" />
                                            </SquareDocThumb>
                                        </ThumbLink>
                                        <UploadAttachmentLabel
                                            htmlFor={fileInputId}
                                        >
                                            <UploadLeft>
                                                <UploadIconWrap>
                                                    <FontAwesomeIcon icon="fa-light fa-paperclip-vertical" />
                                                </UploadIconWrap>
                                                <UploadTextWrap>
                                                    <UploadMainText>
                                                        Reemplazar archivo
                                                    </UploadMainText>
                                                    <UploadSubText>
                                                        PDF, imagen, Word, Excel
                                                        o PowerPoint
                                                    </UploadSubText>
                                                </UploadTextWrap>
                                            </UploadLeft>
                                        </UploadAttachmentLabel>
                                    </MediaReplaceRow>
                                </AttachmentCurrentContent>
                            ) : null}

                            {!selectedFile && !hasCurrentFile && (
                                <UploadAttachmentLabel htmlFor={fileInputId}>
                                    <UploadLeft>
                                        <UploadIconWrap>
                                            <FontAwesomeIcon icon="fa-light fa-paperclip-vertical" />
                                        </UploadIconWrap>
                                        <UploadTextWrap>
                                            <UploadMainText>
                                                Seleccionar archivo
                                            </UploadMainText>
                                            <UploadSubText>
                                                PDF, imagen, Word, Excel o
                                                PowerPoint
                                            </UploadSubText>
                                        </UploadTextWrap>
                                    </UploadLeft>
                                </UploadAttachmentLabel>
                            )}

                            <HiddenAttachmentInput
                                id={fileInputId}
                                type="file"
                                onChange={handleHiddenFileChange}
                                onClick={(event) => {
                                    event.currentTarget.value = '';
                                }}
                                disabled={isLoading}
                            />
                        </MediaBlock>

                        <MediaBlock>
                            <BlockHeaderRow>
                                <MediaKindLabel>Audio</MediaKindLabel>
                                {selectedAudio instanceof Blob && (
                                    <ClearAttachmentButton
                                        type="button"
                                        onClick={() => onAudioChange?.(null)}
                                        disabled={isLoading}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                                        Quitar
                                    </ClearAttachmentButton>
                                )}
                            </BlockHeaderRow>
                            {selectedAudio instanceof Blob ? (
                                <AttachmentCurrentContent>
                                    <AttachmentFileChip>
                                        Audio nuevo
                                    </AttachmentFileChip>
                                </AttachmentCurrentContent>
                            ) : null}
                            {audioUrl && !selectedAudio ? (
                                <MediaReplaceRow>
                                    <ThumbLink
                                        href={audioUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Abrir audio"
                                    >
                                        <SquareMediaThumb>
                                            <FontAwesomeIcon icon="fa-solid fa-volume" />
                                        </SquareMediaThumb>
                                    </ThumbLink>
                                    <RecordZoneShell
                                        $disabled={isLoading}
                                        onClick={handleAudioZoneClick}
                                        role="presentation"
                                    >
                                        <DashedPickerInner>
                                            <PickerTriggerWrap>
                                                <PickRecAudio
                                                    key={`edit-audio-${mediaPickerResetKey}-${selectedAudio ? 'r' : 'n'}`}
                                                    ref={pickAudioRef}
                                                    handleSendAudio={
                                                        onAudioChange
                                                    }
                                                />
                                            </PickerTriggerWrap>
                                            <UploadTextWrap>
                                                <UploadMainText>
                                                    Grabar nuevo audio
                                                </UploadMainText>
                                                <UploadSubText>
                                                    Pulsa aquí o el micrófono.
                                                    Termina en el diálogo y se
                                                    enviará al guardar.
                                                </UploadSubText>
                                            </UploadTextWrap>
                                        </DashedPickerInner>
                                    </RecordZoneShell>
                                </MediaReplaceRow>
                            ) : null}
                            {(!audioUrl || selectedAudio) && (
                                <RecordZoneShell
                                    $disabled={isLoading}
                                    onClick={handleAudioZoneClick}
                                    role="presentation"
                                >
                                    <DashedPickerInner>
                                        <PickerTriggerWrap>
                                            <PickRecAudio
                                                key={`edit-audio-full-${mediaPickerResetKey}-${selectedAudio ? 'r' : 'n'}`}
                                                ref={pickAudioRef}
                                                handleSendAudio={onAudioChange}
                                            />
                                        </PickerTriggerWrap>
                                        <UploadTextWrap>
                                            <UploadMainText>
                                                {audioUrl
                                                    ? 'Grabar otro audio'
                                                    : 'Grabar audio'}
                                            </UploadMainText>
                                            <UploadSubText>
                                                Pulsa aquí o el micrófono.
                                                Confirma en el diálogo; se
                                                enviará al guardar.
                                            </UploadSubText>
                                        </UploadTextWrap>
                                    </DashedPickerInner>
                                </RecordZoneShell>
                            )}
                        </MediaBlock>

                        <MediaBlock>
                            <BlockHeaderRow>
                                <MediaKindLabel>Video</MediaKindLabel>
                                {selectedVideo instanceof Blob && (
                                    <ClearAttachmentButton
                                        type="button"
                                        onClick={() => onVideoChange?.(null)}
                                        disabled={isLoading}
                                    >
                                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                                        Quitar
                                    </ClearAttachmentButton>
                                )}
                            </BlockHeaderRow>
                            {selectedVideo instanceof Blob ? (
                                <AttachmentCurrentContent>
                                    <AttachmentFileChip>
                                        Video nuevo
                                    </AttachmentFileChip>
                                </AttachmentCurrentContent>
                            ) : null}
                            {videoUrl && !selectedVideo ? (
                                <MediaReplaceRow>
                                    <ThumbLink
                                        href={videoUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label="Abrir video"
                                    >
                                        <VideoPosterWrap>
                                            <video
                                                src={videoUrl}
                                                muted
                                                playsInline
                                                preload="metadata"
                                            />
                                        </VideoPosterWrap>
                                    </ThumbLink>
                                    <RecordZoneShell
                                        $disabled={isLoading}
                                        onClick={handleVideoZoneClick}
                                        role="presentation"
                                    >
                                        <DashedPickerInner>
                                            <PickerTriggerWrap>
                                                <PickRecVideo
                                                    key={`edit-video-${mediaPickerResetKey}-${selectedVideo ? 'r' : 'n'}`}
                                                    ref={pickVideoRef}
                                                    handleSendVideo={
                                                        onVideoChange
                                                    }
                                                />
                                            </PickerTriggerWrap>
                                            <UploadTextWrap>
                                                <UploadMainText>
                                                    Grabar nuevo video
                                                </UploadMainText>
                                                <UploadSubText>
                                                    Pulsa aquí o la cámara.
                                                    Confirma en el diálogo y se
                                                    enviará al guardar.
                                                </UploadSubText>
                                            </UploadTextWrap>
                                        </DashedPickerInner>
                                    </RecordZoneShell>
                                </MediaReplaceRow>
                            ) : null}
                            {(!videoUrl || selectedVideo) && (
                                <RecordZoneShell
                                    $disabled={isLoading}
                                    onClick={handleVideoZoneClick}
                                    role="presentation"
                                >
                                    <DashedPickerInner>
                                        <PickerTriggerWrap>
                                            <PickRecVideo
                                                key={`edit-video-full-${mediaPickerResetKey}-${selectedVideo ? 'r' : 'n'}`}
                                                ref={pickVideoRef}
                                                handleSendVideo={onVideoChange}
                                            />
                                        </PickerTriggerWrap>
                                        <UploadTextWrap>
                                            <UploadMainText>
                                                {videoUrl
                                                    ? 'Grabar otro video'
                                                    : 'Grabar video'}
                                            </UploadMainText>
                                            <UploadSubText>
                                                Pulsa aquí o la cámara. Confirma
                                                en el diálogo; se enviará al
                                                guardar.
                                            </UploadSubText>
                                        </UploadTextWrap>
                                    </DashedPickerInner>
                                </RecordZoneShell>
                            )}
                        </MediaBlock>
                    </AttachmentEditorPanel>
                )}

                <Actions>
                    <CommonButton
                        variant="outlined"
                        label="Cancelar"
                        type="button"
                        onClick={onClose}
                        disabled={isLoading}
                    />
                    <CommonButton
                        variant="filled"
                        label="Guardar cambios"
                        type="button"
                        onClick={onSubmit}
                        disabled={isSaveDisabled}
                    />
                </Actions>
            </ModalCard>
        </StyledModal>
    );
};

export default EditCommentModal;

const StyledModal = styled(Modal)`
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
`;

const ModalCard = styled.div`
    width: min(560px, 92vw);
    max-height: 90vh;
    overflow-x: hidden;
    overflow-y: auto;
    background: #ffffff;
    border-radius: 24px;
    padding: 24px;
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    gap: 16px;
    outline: none;
    box-sizing: border-box;
`;

const TextAreaWrap = styled.div`
    flex-shrink: 0;
    width: 100%;
    overflow: visible;
    position: relative;
    z-index: 1;
`;

const Header = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
`;

const Title = styled.h3`
    margin: 0;
    color: #222222;
    font-size: 20px;
    font-weight: 700;
`;

const CloseAction = styled.button`
    border: none;
    background: transparent;
    color: #616161;
    font-size: 20px;
    line-height: 1;
    padding: 0;
    cursor: pointer;
    align-self: flex-start;

    &:hover {
        color: #222222;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const EditTextArea = styled.textarea`
    display: block;
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    min-height: 160px;
    max-height: min(55vh, 520px);
    resize: both;
    overflow: auto;
    border-radius: 18px;
    border: 1px solid #d0d0d0;
    padding: 14px 16px;
    font-size: 15px;
    font-family: inherit;
    color: #222222;
    box-sizing: border-box;
    background: #ffffff;

    &:focus {
        outline: none;
        border-color: #b31d15;
    }

    &:disabled {
        background: #f7f7f7;
        cursor: not-allowed;
        resize: none;
    }
`;

const AttachmentEditorPanel = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    border: 1px solid #e8e8e8;
    border-radius: 14px;
    padding: 10px;
    background: #fafafa;
`;

const PanelSectionLabel = styled.span`
    color: #424242;
    font-size: 13px;
    font-weight: 700;
`;

const MediaBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px;
    border-radius: 12px;
    background: #ffffff;
    border: 1px solid #ececec;
`;

const MediaKindLabel = styled.span`
    font-size: 11px;
    font-weight: 700;
    color: #616161;
    letter-spacing: 0.06em;
    text-transform: uppercase;
`;

const BlockHeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
`;

const RecordZoneShell = styled.div`
    border: 1px dashed #c4c4c4;
    border-radius: 10px;
    background: #ffffff;
    padding: 12px 14px;
    box-sizing: border-box;
    width: 100%;
    transition:
        border-color 0.2s ease,
        background 0.2s ease;
    cursor: pointer;
    opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
    pointer-events: ${(p) => (p.$disabled ? 'none' : 'auto')};

    &:hover {
        border-color: ${(p) => (p.$disabled ? '#c4c4c4' : '#b31d15')};
        background: ${(p) => (p.$disabled ? '#ffffff' : '#fff8f8')};
    }
`;

const DashedPickerInner = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    width: 100%;
    min-width: 0;

    @media (max-width: 420px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const PickerTriggerWrap = styled.div`
    flex-shrink: 0;
    display: flex;
    align-items: center;
`;

const AttachmentCurrentContent = styled.div`
    display: flex;
    align-items: center;
    min-height: 32px;
    width: 100%;
`;

const MediaReplaceRow = styled.div`
    display: grid;
    grid-template-columns: 88px minmax(0, 1fr);
    gap: 12px;
    width: 100%;
    align-items: center;
`;

const SquareMediaThumb = styled.div`
    width: 88px;
    height: 88px;
    border-radius: 10px;
    background: #f5f5f5;
    border: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b31d15;
    font-size: 2rem;
`;

const SquareDocThumb = styled.div`
    width: 88px;
    height: 88px;
    border-radius: 10px;
    background: #f5f5f5;
    border: 1px solid #e8e8e8;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b31d15;
    font-size: 2rem;
`;

const VideoPosterWrap = styled.div`
    width: 88px;
    height: 88px;
    border-radius: 10px;
    overflow: hidden;
    background: #1a1a1a;

    video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        pointer-events: none;
    }
`;

const ThumbLink = styled.a`
    display: block;
    width: 88px;
    height: 88px;
    border-radius: 10px;
    overflow: hidden;
    flex-shrink: 0;
    cursor: pointer;
    line-height: 0;
`;

const ReadonlyAttachmentPreview = styled.img`
    width: 88px;
    height: 88px;
    object-fit: cover;
    display: block;
`;

const HiddenAttachmentInput = styled.input`
    display: none;
`;

const UploadAttachmentLabel = styled.label`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    cursor: pointer;
    border-radius: 10px;
    border: 1px dashed #c4c4c4;
    background: #ffffff;
    padding: 10px;
    width: 100%;
    box-sizing: border-box;
    transition:
        border-color 0.2s ease,
        background 0.2s ease;

    &:hover {
        border-color: #b31d15;
        background: #fff8f8;
    }
`;

const UploadLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const UploadIconWrap = styled.span`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: #f0f0f0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #b31d15;
    flex-shrink: 0;
`;

const UploadTextWrap = styled.span`
    display: inline-flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
`;

const UploadMainText = styled.span`
    color: #222222;
    font-size: 13px;
    font-weight: 700;
`;

const UploadSubText = styled.span`
    color: #757575;
    font-size: 11px;
`;

const AttachmentFileChip = styled.span`
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #333333;
    font-size: 12px;
    background: #ffffff;
    border: 1px solid #e0e0e0;
    border-radius: 999px;
    padding: 5px 10px;
`;

const ClearAttachmentButton = styled.button`
    border: 1px solid #d0d0d0;
    background: #ffffff;
    color: #424242;
    cursor: pointer;
    font-size: 12px;
    font-weight: 700;
    border-radius: 999px;
    padding: 6px 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-shrink: 0;

    &:hover {
        border-color: #b31d15;
        color: #b31d15;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    flex-shrink: 0;

    @media (max-width: 768px) {
        flex-direction: column-reverse;

        > button {
            width: 100%;
        }
    }
`;
